import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material"
import { useLocation, useNavigate, useParams } from "react-router"
import { useEffect, useState } from "react"
import BusinessLinesTable from "./BusinessLinesTable"
import ContractsTable from "./ContractsTable"
import DeleteDialog from "@components/DeleteDialog"
import DiscardDialog from "@components/DiscardDialog"
import {
  organisationsApi,
  useDeleteOrganisationMutation,
  useGetOrganisationByKeyQuery,
  usePostOrganisationMutation,
  usePutOrganisationMutation,
} from "@store/services/organisationService"
import { OrganisationDto } from "@custom-types/organisation"
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form"
import SuccessDialog from "@components/SuccessDialog"
import { useAppDispatch } from "@store/hooks"
import {
  useDeleteDivisionMutation,
  usePostDivisionMutation,
  usePutDivisionMutation,
} from "@store/services/divisionService"
import { getDivisionsToDelete } from "@features/administration/organisations/const"
import UsersTable from "@features/administration/users/UsersTable"

export default function OrganisationForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [isEdit, setIsEdit] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDiscardOpen, setIsDiscardOpen] = useState(false)
  const [isSuccessOpen, setIsSuccessOpen] = useState(false)
  const [postOrganisation, { isLoading: isPostOrgLoading }] = usePostOrganisationMutation()
  const [putOrganisation, { isLoading: isPutOrgLoading }] = usePutOrganisationMutation()
  const [postDivision, { isLoading: isPostDivisionLoading }] = usePostDivisionMutation()
  const [putDivision, { isLoading: isPutDivisionLoading }] = usePutDivisionMutation()
  const [deleteOrganisation, { isLoading: isDeleteOrgLoading }] = useDeleteOrganisationMutation()
  const [deleteDivisionMutation, { isLoading: isDeleteDivisionLoading }] =
    useDeleteDivisionMutation()
  const dispatch = useAppDispatch()

  const methods = useForm<OrganisationDto>({
    defaultValues: {
      name: "",
      key: "",
      description: "",
      active: true,
      creator_email: "",
      creation_date: "",
      last_updated: "",
      email_domains: [],
      total_contracts: 0,
      total_users: 0,
      total_divisions: 0,
      divisions: [],
      users: [],
      contracts: [],
    },
    mode: "onBlur",
  })

  const { control, handleSubmit, reset, watch, getValues, setValue } = methods
  const watchDivisions = watch("divisions")
  const watchUsers = watch("users")
  const watchContracts = watch("contracts")

  // TODO KK: refactor how this form works, making so many requests at submission is not the right approach
  // a) handle all updates at once on BE side
  // b) make corresponding requests "live" as user does changes
  const submitForm = async (data: OrganisationDto) => {
    data.divisions = data.divisions || []
    data.divisions.forEach((item) => {
      item.organisation_key = `${id}`
    })

    if (!isEdit) {
      try {
        await postOrganisation(data).unwrap()
        setIsSuccessOpen(true)
      } catch (error) {
        return
      }
    } else {
      const existingDivisions = organisationData?.divisions ?? []
      const updatedDivisions = data.divisions ?? []
      const deleteDivisionPromises = getDivisionsToDelete(existingDivisions, updatedDivisions).map(
        (division) =>
          deleteDivisionMutation({
            divisionKey: division.key,
            organisationKey: division.organisation_key,
          }).unwrap(),
      )

      const postPutDivisionPromises = data.divisions.map((division) => {
        const isExistingDivision = division.creation_date && division.creator_email
        return isExistingDivision ? putDivision(division).unwrap() : postDivision(division).unwrap()
      })
      await Promise.all([...postPutDivisionPromises, ...deleteDivisionPromises])

      try {
        await putOrganisation(data).unwrap()
        setIsSuccessOpen(true)
      } catch (error) {
        return
      }
    }
  }

  const onSubmit: SubmitHandler<OrganisationDto> = (data) => submitForm(data)

  const { data: organisationData, isFetching: isFetchingOrganisation } =
    useGetOrganisationByKeyQuery(
      {
        key: id || "",
      },
      { skip: id === "new" },
    )

  const isFetching =
    isPostOrgLoading ||
    isPutOrgLoading ||
    isDeleteOrgLoading ||
    isPostDivisionLoading ||
    isPutDivisionLoading ||
    isDeleteDivisionLoading

  useEffect(() => {
    if (id !== "new") {
      if (organisationData) {
        const data = structuredClone(organisationData)
        data.divisions = organisationData.divisions?.map((division, index) => {
          return {
            ...division,
            id: index,
          }
        })
        setIsEdit(true)
        reset(data)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [organisationData])

  const onCancelClick = () => {
    setIsDiscardOpen(true)
  }

  const onAddUserClick = () => {
    navigate(`/Administration/Users/new`, {
      state: { orgId: id, from: location },
    })
  }

  const onAddContractClick = () => {
    navigate(`/Administration/Contracts/new`)
  }

  const handleOrganisationDelete = async () => {
    setIsDeleteOpen(false)
    await deleteOrganisation(getValues().key).unwrap()
    setIsSuccessOpen(true)
  }

  return (
    <Box>
      <Typography variant={"h6"} sx={{ mt: 2, mb: 1 }}>
        Organisation Form
      </Typography>
      {isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <FormProvider {...methods}>
          <form autoComplete={"off"} onSubmit={handleSubmit(onSubmit)}>
            <Grid container={true} spacing={1}>
              <Grid item={true} xs={6}>
                <Controller
                  name={"name"}
                  control={control}
                  rules={{ required: true, maxLength: 30 }}
                  render={({
                    field: { value, onChange, disabled, onBlur },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      fullWidth={true}
                      required={true}
                      type="text"
                      variant="outlined"
                      label="Name"
                      onChange={onChange}
                      disabled={disabled}
                      inputProps={{ maxLength: 30 }}
                      value={value}
                      error={invalid}
                      helperText={error?.message}
                      onBlur={onBlur}
                    />
                  )}
                />
              </Grid>
              <Grid item={true} xs={6}>
                <Controller
                  name={"key"}
                  control={control}
                  rules={{ required: true }}
                  render={({
                    field: { value, onChange, disabled, onBlur },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      fullWidth={true}
                      required={true}
                      type="text"
                      variant="outlined"
                      label="Mnemonic"
                      onChange={(e) => {
                        if (!e.target.value.match(/^[a-zA-Z0-9\-]+$|^(?=$)/)) {
                          return
                        }
                        onChange(e)
                      }}
                      inputProps={{ maxLength: 10 }}
                      disabled={disabled}
                      value={value}
                      error={invalid}
                      helperText={error?.message}
                      onBlur={onBlur}
                    />
                  )}
                />
              </Grid>
              <Grid item={true} xs={6}>
                <Controller
                  name={"description"}
                  control={control}
                  rules={{ required: true, maxLength: 500 }}
                  render={({
                    field: { value, onChange, disabled, onBlur },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      fullWidth={true}
                      required={true}
                      type="text"
                      variant="outlined"
                      label="Description"
                      onChange={onChange}
                      inputProps={{ maxLength: 500 }}
                      disabled={disabled}
                      value={value}
                      error={invalid}
                      multiline={true}
                      rows={2}
                      helperText={error?.message}
                      onBlur={onBlur}
                    />
                  )}
                />
              </Grid>
              <Grid item={true} xs={6}>
                <Controller
                  name={"email_domains"}
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      if (value?.length > 5) {
                        return false
                      }

                      if (Array.isArray(value)) {
                        let isValid = true
                        const regex = /^@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/g
                        value.forEach((item) => {
                          const found = item.match(regex)
                          if (!found) {
                            isValid = false
                          }
                        })
                        if (!isValid) {
                          return false
                        }
                      }

                      return true
                    },
                  }}
                  render={({
                    field: { value, onChange, disabled, onBlur },
                    fieldState: { error, invalid },
                  }) => (
                    <Autocomplete
                      style={{ margin: "10px 0" }}
                      multiple={true}
                      limitTags={5}
                      id="tags-outlined"
                      options={value}
                      defaultValue={value}
                      freeSolo={true}
                      autoSelect={true}
                      onChange={(e, data) => {
                        onChange(data)
                      }}
                      value={value}
                      onBlur={onBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Domains"
                          placeholder="Domains"
                          variant={"outlined"}
                          required={true}
                          error={invalid}
                          helperText={error?.message ? "Incorrect Domain" : undefined}
                          inputProps={{
                            ...params.inputProps,
                            required: value?.length === 0,
                          }}
                          fullWidth={true}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item={true} xs={12}>
                <Controller
                  name={"active"}
                  control={control}
                  render={({
                    field: { value, onChange, disabled, onBlur },
                    fieldState: { error, invalid },
                  }) => (
                    <FormControlLabel
                      labelPlacement={"start"}
                      control={<Checkbox onChange={onChange} checked={value} onBlur={onBlur} />}
                      label="Active"
                    />
                  )}
                />
              </Grid>
              <Grid item={true} xs={12}>
                {isEdit && (
                  <>
                    <Box>
                      <Typography variant={"h6"}>Business Lines</Typography>
                      <BusinessLinesTable
                        data={watchDivisions || []}
                        setData={(newData) => {
                          setValue("divisions", newData)
                        }}
                      />
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant={"h6"}>Contracts</Typography>
                      <ContractsTable data={watchContracts || []} orgId={id} />
                      <Button sx={{ mt: 1 }} onClick={onAddContractClick} variant="contained">
                        Add contract
                      </Button>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant={"h6"}>Users</Typography>
                      <UsersTable
                        data={watchUsers || []}
                        onEditClick={(id) =>
                          navigate(`/Administration/Users/${id}`, {
                            state: { from: location },
                          })
                        }
                      />
                      <Button
                        sx={{ mt: 1 }}
                        onClick={onAddUserClick}
                        disabled={watchDivisions?.length === 0}
                        variant="contained"
                      >
                        Add user
                      </Button>
                    </Box>
                  </>
                )}
              </Grid>
              <Grid item={true} xs={12}>
                <Button variant="contained" type="submit">
                  Save
                </Button>
                <Button
                  sx={{ ml: 1 }}
                  variant="contained"
                  color="error"
                  type="button"
                  onClick={onCancelClick}
                >
                  Cancel
                </Button>
                {isEdit && (
                  <Button
                    sx={{ ml: 1 }}
                    variant="contained"
                    color="error"
                    type="button"
                    onClick={() => {
                      setIsDeleteOpen(true)
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      )}
      <DeleteDialog
        open={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        onDeleteClick={handleOrganisationDelete}
      />
      <DiscardDialog
        open={isDiscardOpen}
        setOpen={setIsDiscardOpen}
        onAcceptClick={() => {
          navigate("/Administration/Organisations")
        }}
      />
      <SuccessDialog
        open={isSuccessOpen}
        setOpen={setIsSuccessOpen}
        onCloseClick={() => {
          dispatch(organisationsApi.util.resetApiState())
          navigate("/Administration/Organisations")
        }}
      />
    </Box>
  )
}
